import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import { dauer, letzteMonate, monatsName, prozent, verfuegbarkeitsStufe } from '../lib/bericht'
import { formatBytes, formatDateTime } from '../lib/format'
import type { Customer, MonthlyReport } from '../lib/types'
import { StatusBadge } from './Status'
import { ErrorBox, Loading } from './Feedback'

const ARTEN: Record<string, string> = { ssh: 'SSH', gui: 'Weboberfläche', portforward: 'Tunnel' }

const FARBEN = { ok: 'var(--ok)', warn: 'var(--warn)', crit: 'var(--crit)', off: 'var(--text)' }

/**
 * Der Monatsbericht eines Kunden: Verfügbarkeit je Gerät, Alarme,
 * Sicherungen, Fernzugriffe und Datenverkehr eines Kalendermonats. Gedruckt
 * (oder als PDF gespeichert) ist er das Dokument für das Kundengespräch.
 */
export function Monatsbericht({ customer, monatVorwahl }: { customer: Customer; monatVorwahl?: string }) {
  const monate = letzteMonate(13)
  const vormonat = monate[1] as string
  const [monat, setMonat] = useState(monatVorwahl && monate.includes(monatVorwahl) ? monatVorwahl : vormonat)
  const { can } = useAuth()

  const bericht = useQuery({
    queryKey: ['monatsbericht', customer.id, monat],
    queryFn: ({ signal }) => apiGet<MonthlyReport>(`/customers/${customer.id}/monatsbericht?monat=${monat}`, signal),
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Monatsbericht {monatsName(monat)}</h1>
          <div className="page-sub">
            {customer.name}
            {bericht.data && !bericht.data.abgeschlossen ? ' · vorläufig, der Monat läuft noch' : ''}
          </div>
        </div>
        <div className="row no-print" style={{ gap: 8 }}>
          <label className="sr-only" htmlFor="bericht-monat">
            Monat
          </label>
          <select
            id="bericht-monat"
            className="input"
            style={{ width: 190, height: 32 }}
            value={monat}
            onChange={(e) => setMonat(e.target.value)}
          >
            {monate.map((m, i) => (
              <option key={m} value={m}>
                {monatsName(m)}
                {i === 0 ? ' (laufend)' : ''}
              </option>
            ))}
          </select>
          <button type="button" className="btn" onClick={() => window.print()}>
            Drucken
          </button>
        </div>
      </div>

      {bericht.isError ? (
        <ErrorBox error={bericht.error} what="Der Monatsbericht" />
      ) : bericht.isPending ? (
        <Loading what="Den Monatsbericht" />
      ) : Array.isArray(bericht.data.geraete) ? (
        <Inhalt r={bericht.data} />
      ) : (
        // Eine Antwort ohne die erwartete Form (etwa von einer älteren
        // Zentrale) nennt das, statt die Seite abstürzen zu lassen.
        <ErrorBox error={new Error('Die Zentrale kennt den Monatsbericht noch nicht.')} what="Der Monatsbericht" />
      )}

      {can('settings.manage') ? <Versand key={customer.id} customer={customer} monat={monat} /> : null}
    </>
  )
}

function Inhalt({ r }: { r: MonthlyReport }) {
  const gesichert = r.sicherungen.filter((s) => s.erfolgreich > 0).length
  const stufe = verfuegbarkeitsStufe(r.verfuegbarkeit)
  return (
    <>
      <div className="kpi-grid">
        <div className="kpi">
          <span className="kpi-label">Verfügbarkeit</span>
          <span className="kpi-value" style={{ color: FARBEN[stufe] }}>
            {prozent(r.verfuegbarkeit)}
          </span>
          <span className="kpi-sub">{r.geraete.length} Geräte, ohne geplante Wartung</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Alarme</span>
          <span className="kpi-value" style={{ color: r.alarme.kritisch > 0 ? 'var(--warn)' : 'var(--ok)' }}>
            {r.alarme.gesamt}
          </span>
          <span className="kpi-sub">
            {r.alarme.kritisch} kritisch
            {r.alarme.mittlere_dauer_s !== null ? ` · im Mittel ${dauer(r.alarme.mittlere_dauer_s)}` : ''}
          </span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Konfiguration gesichert</span>
          <span className="kpi-value" style={{ color: r.sicherung_fehlt > 0 ? 'var(--warn)' : 'var(--ok)' }}>
            {gesichert} / {r.sicherungen.length}
          </span>
          <span className="kpi-sub">
            {r.sicherung_fehlt > 0 ? `${r.sicherung_fehlt} ohne Sicherung im Monat` : 'alle Geräte gesichert'}
          </span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Fernzugriffe</span>
          <span className="kpi-value">{r.fernzugriffe_anzahl}</span>
          <span className="kpi-sub">zusammen {dauer(r.fernzugriffe_s)}</span>
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Verfügbarkeit je Gerät</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Wartungsfenster und Zeiten ohne Agent zählen nicht als Ausfall
          </span>
        </div>
        <table className="data" aria-label="Verfügbarkeit je Gerät">
          <thead>
            <tr>
              <th>Gerät</th>
              <th>Standort</th>
              <th style={{ textAlign: 'right' }}>Verfügbarkeit</th>
              <th style={{ textAlign: 'right' }}>Ausfälle</th>
              <th style={{ textAlign: 'right' }}>Ausfallzeit</th>
              <th style={{ textAlign: 'right' }}>Wartung</th>
              <th style={{ textAlign: 'right' }}>Ohne Messung</th>
            </tr>
          </thead>
          <tbody>
            {r.geraete.map((g) => (
              <tr key={g.id}>
                <td>{g.name}</td>
                <td className="muted">{g.standort}</td>
                <td
                  className="mono"
                  style={{ textAlign: 'right', color: FARBEN[verfuegbarkeitsStufe(g.verfuegbarkeit)] }}
                >
                  {prozent(g.verfuegbarkeit)}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {g.ausfaelle}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {g.ausfall_s > 0 ? dauer(g.ausfall_s) : '–'}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {g.wartung_s > 0 ? dauer(g.wartung_s) : '–'}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {g.ohne_messung_s > 0 ? dauer(g.ohne_messung_s) : '–'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="row" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <section className="card" style={{ flexGrow: 1, minWidth: 320 }}>
          <div className="card-head">
            <h2 className="card-title">Häufigste Alarme</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              {r.alarme.unterdrueckt > 0 ? `${r.alarme.unterdrueckt} Folgealarme und Wartung nicht mitgezählt` : ''}
            </span>
          </div>
          {r.alarme.haeufigste.length === 0 ? (
            <div className="empty">
              <span>Keine Alarme in diesem Monat.</span>
            </div>
          ) : (
            <table className="data" aria-label="Häufigste Alarme">
              <tbody>
                {r.alarme.haeufigste.map((a) => (
                  <tr key={`${a.geraet}-${a.titel}`}>
                    <td className="mono" style={{ width: 50 }}>
                      {a.anzahl}×
                    </td>
                    <td>
                      {a.titel}
                      {a.kritisch ? (
                        <span style={{ marginLeft: 8 }}>
                          <StatusBadge state="crit" label="kritisch" />
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card" style={{ flexGrow: 1, minWidth: 320 }}>
          <div className="card-head">
            <h2 className="card-title">Konfigurationssicherung</h2>
          </div>
          {r.sicherungen.length === 0 ? (
            <div className="empty">
              <span>Kein Gerät ist für die Sicherung eingerichtet.</span>
            </div>
          ) : (
            <table className="data" aria-label="Konfigurationssicherung">
              <thead>
                <tr>
                  <th>Gerät</th>
                  <th style={{ textAlign: 'right' }}>Erfolgreich</th>
                  <th style={{ textAlign: 'right' }}>Fehlgeschlagen</th>
                  <th style={{ textAlign: 'right' }}>Änderungen</th>
                  <th>Letzte</th>
                </tr>
              </thead>
              <tbody>
                {r.sicherungen.map((s) => (
                  <tr key={s.device_id}>
                    <td>{s.geraet}</td>
                    <td
                      className="mono"
                      style={{ textAlign: 'right', color: s.erfolgreich === 0 ? 'var(--crit)' : undefined }}
                    >
                      {s.erfolgreich}
                    </td>
                    <td
                      className="mono"
                      style={{ textAlign: 'right', color: s.fehlgeschlagen > 0 ? 'var(--warn)' : undefined }}
                    >
                      {s.fehlgeschlagen}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {s.aenderungen}
                    </td>
                    <td className="muted">{s.letzte_erfolgreiche ? formatDateTime(s.letzte_erfolgreiche) : 'keine'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Fernzugriffe</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Wer wann auf welches Gerät zugegriffen hat
          </span>
        </div>
        {r.fernzugriffe.length === 0 ? (
          <div className="empty">
            <span>Kein Fernzugriff in diesem Monat.</span>
          </div>
        ) : (
          <table className="data" aria-label="Fernzugriffe">
            <thead>
              <tr>
                <th>Beginn</th>
                <th>Benutzer</th>
                <th>Gerät</th>
                <th>Art</th>
                <th style={{ textAlign: 'right' }}>Dauer</th>
              </tr>
            </thead>
            <tbody>
              {r.fernzugriffe.map((f) => (
                <tr key={`${f.beginn}-${f.benutzer}-${f.geraet}`}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(f.beginn)}
                  </td>
                  <td>{f.benutzer}</td>
                  <td>{f.geraet}</td>
                  <td>{ARTEN[f.art] ?? f.art}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {dauer(f.dauer_s)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {r.datenverkehr.length > 0 ? (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Datenverkehr</h2>
          </div>
          <table className="data" aria-label="Datenverkehr je Gerät">
            <tbody>
              {r.datenverkehr.map((d) => (
                <tr key={d.geraet}>
                  <td>{d.geraet}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {formatBytes(d.bytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </>
  )
}

/** Wer den Bericht am Monatsersten bekommt, und ein Versand auf Knopfdruck. */
function Versand({ customer, monat }: { customer: Customer; monat: string }) {
  const queryClient = useQueryClient()
  const aktuell = customer.report_recipients ?? ''
  const [empfaenger, setEmpfaenger] = useState(aktuell)
  const speichern = useMutation({
    mutationFn: () => apiSend<{ empfaenger: string }>('PUT', `/customers/${customer.id}/monatsbericht`, { empfaenger }),
    onSuccess: (res) => {
      setEmpfaenger(res.empfaenger)
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
  })
  const senden = useMutation({
    mutationFn: () =>
      apiSend<{ empfaenger: string }>('POST', `/customers/${customer.id}/monatsbericht/senden`, { monat }),
  })

  return (
    <section className="card no-print">
      <div className="card-head">
        <h2 className="card-title">Versand per E-Mail</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Am Monatsersten ab 6 Uhr geht der Bericht des Vormonats an diese Adressen
        </span>
      </div>
      <form
        className="card-body row"
        style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          speichern.mutate()
        }}
      >
        <div className="field" style={{ flex: 1, minWidth: 280 }}>
          <label htmlFor="bericht-empfaenger">Empfänger (durch Komma getrennt, leer für keinen Versand)</label>
          <input
            id="bericht-empfaenger"
            className="input"
            value={empfaenger}
            placeholder="it@kunde.at, geschaeftsfuehrung@kunde.at"
            onChange={(e) => setEmpfaenger(e.target.value)}
          />
        </div>
        <button type="submit" className="btn" disabled={empfaenger === aktuell || speichern.isPending}>
          Empfänger speichern
        </button>
        <button type="button" className="btn" disabled={!aktuell || senden.isPending} onClick={() => senden.mutate()}>
          Jetzt senden
        </button>
      </form>
      <div className="card-body" style={{ paddingTop: 0 }}>
        {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
        {senden.isError ? <span style={{ color: 'var(--crit)' }}>{(senden.error as Error).message}</span> : null}
        {senden.isSuccess ? (
          <span style={{ color: 'var(--ok)' }}>
            Bericht {monatsName(monat)} an {senden.data.empfaenger} versendet.
          </span>
        ) : null}
      </div>
    </section>
  )
}
